/* eslint-disable @typescript-eslint/naming-convention */
import { Color, PaletteColor } from '@mui/material';
import { green, grey, indigo, orange, pink, red } from '@mui/material/colors';
import { alpha, createTheme, PaletteColorOptions, ThemeOptions } from '@mui/material/styles';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import React, { ForwardedRef } from 'react';
import { Link } from 'react-router-dom';

import color from './color.json';

type ColorPartial = Partial<Color>;

declare module '@mui/material' {
  interface PaletteColor extends ColorPartial {
    light: string;
  }
}

declare module '@mui/material/styles' {
  interface TypeBackground {
    ultralight?: string;
    verylight?: string;
    sidebar?: string;
    input?: string;
  }
  interface TypeText {
    secondarySelected?: string;
  }
  interface PaletteOptions {
    responsive: {
      grey: PaletteColorOptions;
    };
  }

  interface Palette {
    responsive: {
      grey: PaletteColor;
    };
  }
}

const CustomLinkComponent = React.forwardRef(function CustomLinkComponent(
  props: { href: string } & React.PropsWithChildren,
  ref: React.ForwardedRef<any>
) {
  const { href, ...rest } = props;
  if (href) return <Link ref={ref as ForwardedRef<HTMLAnchorElement>} to={href} {...rest} />;
  else return <div ref={ref as any} {...rest} />;
});

const themeBase: ThemeOptions = {
  color,
  typography: {
    fontFamily: 'Poppins',
    body1: {
      fontSize: '14px',
      lineHeight: '1',
    },
  },
  palette: {
    action: {
      selectedOpacity: 0.08,
      active: '#000',
      selected: '#000',
      hover: '#0000000d',
      hoverOpacity: 0.08,
    },
    primary: {
      main: '#000',
      50: '#ededed',
      100: '#222',
      200: '#333',
      300: '#444',
      400: '#555',
      500: '#666',
      600: '#777',
      700: '#888',
      800: '#ebebeb',
      900: '#f5f5f5',
    },
    divider: '#e4e4e7',
    secondary: pink,
    error: red,
    warning: orange,
    success: green,
    text: {
      primary: '#000',
      secondary: '#71717b',
      secondarySelected: '#bbb',
      disabled: '#aaa',
    },
    background: {
      verylight: '#fafafa',
      ultralight: grey[50],
      sidebar: '#fafafa',
      input: '#00000000',
    },
    responsive: {
      grey: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        50: grey[50],
      },
    },
  },
  shape: {
    borderRadius: 8,
  },

  components: {
    MuiTypography: {
      styleOverrides: {
        body1: {
          '&:not(span)': {
            lineHeight: 1.5,
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          overflowY: 'auto',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          overflowY: 'auto',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        LinkComponent: CustomLinkComponent,
      },
      styleOverrides: {
        root: {
          transition: 'background 200ms ease !important',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          '&.MuiLoadingButton-root': {
            transitionBehavior: 'normal',
            transitionDelay: '0s',
            transitionDuration: '0.25s',
            transitionProperty: 'background-color, box-shadow, border-color, color',
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
          },
        },
        outlined: ({ theme }) => ({
          boxShadow: `rgba(0, 0, 0, 0) 0px 0px 0px 0px, 
                rgba(0, 0, 0, 0) 0px 0px 0px 0px, 
                rgba(0, 0, 0, 0) 0px 0px 0px 0px, 
                rgba(0, 0, 0, 0) 0px 0px 0px 0px, 
                rgba(0, 0, 0, 0.05) 0px 1px 2px 0px`,
          borderColor: theme.palette.divider,
          '&:hover': {
            backgroundColor: theme.palette.background.ultralight,
            '&:not(.Mui-error)': {
              borderColor: theme.palette.divider,
            },
          },
        }),
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '& .MuiButtonBase-root': {
            padding: '8px !important',
            borderRadius: '8px !important',
          },
          '& .MuiListItemText-root': {
            margin: 0,
            lineHeight: '1 !important',
          },
          '& .MuiSvgIcon-root': {
            fontSize: '16px',
          },
          '& .MuiListItemIcon-root': {
            minWidth: '24px !important',
          },
        },
      },
    },
    MuiListItemButton: {
      defaultProps: {
        // eslint-disable-next-line
        // @ts-ignore
        component: CustomLinkComponent,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '&.active:not(#a), &.Mui-selected:not(#a)': {
            // backgroundColor: theme.palette.action.selected,
            ...(theme.palette.mode === 'dark'
              ? {
                  backgroundColor: theme.palette.primary[800],
                }
              : {
                  backgroundColor: alpha(theme.palette.action.active, 0.04),
                }),
          },
          '&.active .MuiListItemText-root': {
            // color: `${theme.palette.primary.contrastText} !important`,
            color: `${theme.palette.text.primary} !important`,
          },
          '&.active .MuiListItemIcon-root': {
            // color: `${theme.palette.primary.contrastText} !important`,
            color: `${theme.palette.text.primary} !important`,
          },
          '&.Mui-selected .MuiListItemText-secondary': {
            // color: theme.palette.text.secondarySelected,
            color: theme.palette.text.secondary,
          },
          '&.MuiButtonBase-root': {
            padding: '8px !important',
            borderRadius: '8px !important',
          },
          '& .MuiListItemText-root': {
            margin: 0,
            lineHeight: '1 !important',
          },
          '& .MuiSvgIcon-root': {
            fontSize: '16px',
          },
          '& .MuiListItemIcon-root': {
            minWidth: '24px !important',
          },
        }),
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        labelContainer: {
          color: indigo[200],
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: indigo[100],
        },
      },
    },
    MuiSkeleton: {
      defaultProps: {
        animation: 'wave',
        variant: 'rounded',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          // backgroundColor: indigo[50],
          backgroundColor: theme.palette.background.verylight,
        }),
        rounded: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
        }),
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          '& a': {
            color: indigo[200],
            textDecoration: 'none',
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          transitionDuration: '200ms',
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1) ',
          transitionDelay: '0ms',
          transitionProperty: 'background-color, color',
          // eslint-disable-next-line @typescript-eslint/naming-convention
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          // eslint-disable-next-line @typescript-eslint/naming-convention
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
          // eslint-disable-next-line @typescript-eslint/naming-convention
          '&.Mui-selected:hover': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
        }),
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiDataGrid-cell': {
            borderColor: theme.palette.divider,
          },
          borderColor: theme.palette.divider,
          '& .MuiDataGrid-columnHeaders': {
            borderBottomColor: theme.palette.divider,
          },
          '& .MuiDataGrid-footerContainer': {
            borderTopColor: theme.palette.divider,
          },
        }),
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          '&#nav-sidebar-list, &#nav-sidebar-list-bottom': {
            paddingLeft: 16,
            paddingRight: 16,
            gap: 8,
            display: 'flex',
            flexDirection: 'column',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiMenu-paper': {
            backgroundColor: theme.palette.background.verylight,
            borderColor: theme.palette.divider,
            borderWidth: 1,
            borderStyle: 'solid',
            padding: 4,
            '.MuiMenu-list': {
              padding: 0,
            },
          },
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: 8,
          borderRadius: theme.shape.borderRadius,
          '&.Mui-selected': {
            // backgroundColor: theme.palette.background.paper,
            backgroundColor: theme.palette.primary[800],
          },
          '&:hover': {
            backgroundColor: theme.palette.primary[800],
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary[800],
            },
          },
          '& > * > img': {
            padding: 1,
          },
        }),
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiDataGrid-menuList, & .MuiAutocomplete-listbox': {
            backgroundColor: theme.palette.background.verylight,
            borderColor: theme.palette.divider,
            borderRadius: theme.shape.borderRadius,
            borderWidth: 1,
            borderStyle: 'solid',
            padding: 4,
            '.MuiDivider-root': {
              margin: '8px 0',
            },
          },
          '& .MuiAutocomplete-option:not(#a)': {
            borderRadius: theme.shape.borderRadius,
            padding: 8,
            '&.Mui-selected, &.Mui-selected:hover, &.Mui-focused, &:hover': {
              backgroundColor: theme.palette.primary[800],
            },
          },
          '& .MuiListItem-root': {
            borderRadius: theme.shape.borderRadius,
          },
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        root: ({ theme }) => ({
          '.MuiFormLabel-root:not(.Mui-focused):not(.MuiFormLabel-filled):not(.MuiInputLabel-shrink)': {
            transform: 'translate(14px, 10px) scale(1)',
          },
        }),
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none !important',
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiPaper-root': {
            borderStyle: 'solid',
            borderColor: theme.palette.divider,
            borderWidth: 1,
          },
        }),
      },
    },
    MuiChip: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiSvgIcon-root': {
            margin: 0,
          },
          height: 'auto',
          padding: 2,
          backgroundColor: theme.palette.background.verylight,
          borderColor: theme.palette.divider,
          borderWidth: 1,
          borderStyle: 'solid',
        }),
      },
    },
    MuiSwitch: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '& .Mui-checked + .MuiSwitch-track': {
            opacity: '1 !important',
            backgroundColor: theme.palette.primary.main,
          },
          '.Mui-checked .MuiSwitch-thumb': {
            backgroundColor: theme.palette.primary.contrastText,
          },
          '& .Mui-checked': {
            transform: 'translateX(20px) !important',
          },
          height: 20,
          padding: 2,
          marginLeft: 4,
          '& .MuiSwitch-track': {
            borderRadius: '100px',
          },
          '& .MuiSwitch-thumb': {
            width: 12,
            height: 12,
          },
        }),
      },
    },
    MuiInputBase: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.input,
          '&:not(.MuiAutocomplete-inputRoot)': {
            padding: '0 !important',
          },
          outlineColor: '#00000026',
          '&.Mui-error': {
            outlineColor: alpha(theme.palette.error.main, 0.25),
          },
          outlineStyle: 'solid',
          outlineWidth: 0,
          transition: 'outline-width 150ms ease',
          '& .MuiInputBase-input': {
            padding: 8,
            paddingLeft: 12,
            borderRadius: theme.shape.borderRadius,
          },
          '&.MuiOutlinedInput-root.Mui-focused': {
            outlineWidth: 3,
          },
          '&.MuiOutlinedInput-root': {
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
              boxShadow: `rgba(0, 0, 0, 0) 0px 0px 0px 0px, 
                rgba(0, 0, 0, 0) 0px 0px 0px 0px, 
                rgba(0, 0, 0, 0) 0px 0px 0px 0px, 
                rgba(0, 0, 0, 0) 0px 0px 0px 0px, 
                rgba(0, 0, 0, 0.05) 0px 1px 2px 0px`,
            },
            '&:hover': {
              backgroundColor: theme.palette.background.ultralight,
              '&:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.divider,
              },
            },
            '&.Mui-focused': {
              '.MuiOutlinedInput-notchedOutline': {
                borderWidth: 1,
              },
              '&:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary[500],
              },
            },
          },
        }),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        multiline: {
          padding: '4px 8px',
          '& .MuiListItemText-secondary': {
            marginTop: 4,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        root: ({ theme }) => ({
          '& .MuiSvgIcon-root': {
            display: 'none',
          },
          '&::after': {
            content: '""',
            display: 'block',
            width: 16,
            height: 16,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: theme.palette.primary.main,
            borderRadius: theme.shape.borderRadius * 0.5,
            color: theme.palette.primary.main,
          },
          '&.Mui-checked::after': {
            color: theme.palette.primary.contrastText,
            ...(theme.palette.mode === 'dark'
              ? {
                  borderColor: 'white',
                  background: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M20 6 9 17l-5-5'/></svg>"), white`,
                }
              : {
                  borderColor: theme.palette.primary.main,
                  background: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M20 6 9 17l-5-5'/></svg>"), black`,
                }),
          },
        }),
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:not(:empty)': {
            borderWidth: 1,
            borderTopStyle: 'solid',
            borderBottomStyle: 'solid',
            borderColor: theme.palette.divider,
            padding: '10px 8px',
            margin: '12px -4px 4px -4px',
            lineHeight: 1,
            fontWeight: 500,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
          },
        }),
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 'auto',
          '.MuiTabs-scroller': {
            padding: '8px 0',
          },
          '.MuiTabs-flexContainer': {
            backgroundColor: theme.palette.primary[900],
            borderRadius: theme.shape.borderRadius,
          },
          '.MuiTabs-indicator': {
            // display: 'none',
            borderRadius: theme.shape.borderRadius,
            backgroundColor: 'transparent',

            zIndex: 1,
            '&:after': {
              content: '""',
              display: 'block',
              width: '100%',
              height: '100%',
              ...(theme.palette.mode === 'dark'
                ? {
                    backgroundColor: theme.palette.primary[800],
                    borderColor: theme.palette.divider,
                    borderWidth: 1,
                    borderStyle: 'solid',
                  }
                : {
                    backgroundColor: theme.palette.background.paper,
                  }),
              borderRadius: theme.shape.borderRadius,
              boxShadow: theme.shadows[2],
            },
          },
          '&.MuiTabs-vertical .MuiTabs-indicator': {
            width: '100%',
            padding: '2px 2px',
          },
          '&:not(.MuiTabs-vertical)': {
            '.MuiTabs-flexContainer': {
              width: 'fit-content',
              alignItems: 'center',
            },
            '.MuiTabs-indicator': {
              height: '100%',
              padding: '10px 2px',
            },
          },

          '.MuiTab-root': {
            minHeight: 'auto',
            padding: '8px 10px',
            zIndex: 2,
          },
        }),
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.primary[900],
          borderRadius: theme.shape.borderRadius,
          borderColor: theme.palette.divider,
          borderWidth: 1,
          borderStyle: 'solid',
          width: 'fit-content',
          padding: '0px !important',
          '.MuiToggleButton-root': {
            padding: '8px 8px',
            margin: 2,
            border: 'none',
            lineHeight: 1,
            '&.Mui-selected': {
              zIndex: 2,

              borderRadius: theme.shape.borderRadius,
              color: theme.palette.text.primary,
              ...(theme.palette.mode === 'dark'
                ? {
                    backgroundColor: theme.palette.primary[800],
                    borderColor: theme.palette.divider,
                    borderWidth: 1,
                    borderStyle: 'solid',
                  }
                : {
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: theme.shadows[2],
                  }),
            },
            '&.Mui-selected:hover': {
              ...(theme.palette.mode === 'dark'
                ? {
                    backgroundColor: theme.palette.primary[800],
                  }
                : {
                    backgroundColor: theme.palette.background.paper,
                  }),
              color: theme.palette.text.primary,
            },
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '&.Mui-disabled': {
              border: 'none',
              backgroundColor: 'transparent',
            },
          },
        }),
      },
    },
    MuiSvgIcon: {
      defaultProps: {
        fontSize: 'small',
      },
    },
  },
};

export const theme = createTheme(themeBase);

export const themeDark = createTheme({
  ...themeBase,
  palette: {
    ...themeBase.palette,
    mode: 'dark',
    responsive: {
      grey: {},
    },
    background: {
      verylight: '#18181b',
      ultralight: '#18181b',
      sidebar: '#18181b',
    },
    action: {
      selectedOpacity: 0.125,
      active: '#fff',
      selected: '#fff',
      hover: '#ffffff0d',
      hoverOpacity: 0.125,
    },
    divider: '#ffffff1a',
    text: {
      primary: '#ffffff',
    },
    primary: {
      // main: '#e4e4e7',
      main: '#fff',
      '50': '#111',
      '100': '#333',
      '200': '#555',
      '300': '#777',
      '400': '#27272a',
      '500': '#aaa',
      '600': '#bbb',
      '700': '#ccc',
      '800': '#333',
      '900': '#262626',
    },

    warning: {
      main: '#ffa500',
      50: '#3b2905',
      100: '#5a3d0a',
      200: '#7a510f',
      300: '#996514',
      400: '#b97919',
      500: '#d88d1e',
      600: '#f7a123',
      700: '#f9b528',
      800: '#fac92d',
      900: '#fcdb32',
    },
    success: {
      main: '#1be11b',
      '50': '#022102',
      '100': '#022102',
      '200': '#022102',
      '300': '#022102',
      '400': '#022102',
      '500': '#1be11b',
      '600': '#54bd54',
      '700': '#54bd54',
      '800': '#54bd54',
      '900': '#54bd54',
    },
  },
});

if (process.env.NODE_ENV !== 'production') {
  //eslint-disable-next-line
  console.log('Current Theme', theme);
  //eslint-disable-next-line
  //console.warn("Note: The current theme is not properly setup. Dark mode, contrast ratios, and many other properties are yet to be configured.");
  //eslint-disable-next-line
  //console.warn("Note: fix typescript for component override in theme MuiButtonBase.");
}
